/**
 * Employee Requirement Detail Page - View employee's training history and manage requirements
 */

import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { learningApi } from '../api/learningApi'
import type { VerificationStatus, CreateTrainingRequirementInput, UpdateTrainingRequirementInput } from '../types/learning.types'
import { useAuth } from '@/core/auth/AuthContext'
import LoadingPage from '@/shared/components/LoadingPage'
import toast from 'react-hot-toast'

const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  pending: 'Pending',
  verified: 'Verified',
  rejected: 'Rejected',
}

const VERIFICATION_STATUS_COLORS: Record<VerificationStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  verified: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
}

const EmployeeRequirementDetailPage = () => {
  const { employeeId } = useParams<{ employeeId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const currentYear = new Date().getFullYear()

  // State
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingRequirement, setEditingRequirement] = useState<{ id: string; required_hours: number } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [formData, setFormData] = useState<CreateTrainingRequirementInput>({
    employee_id: employeeId || '',
    year: currentYear,
    required_hours: 40,
  })
  const [editFormData, setEditFormData] = useState<UpdateTrainingRequirementInput>({
    required_hours: 0,
  })

  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i + 1)

  // Check if user is admin or HR
  const isAdminOrHR = user?.roles?.some(r => r.name === 'admin' || r.name === 'hr') ||
    user?.division_name?.toLowerCase() === 'human resources'

  // Redirect if not admin/HR
  useEffect(() => {
    if (user && !isAdminOrHR) {
      navigate('/learning')
    }
  }, [user, isAdminOrHR, navigate])

  // Fetch all requirements for this employee (all years)
  const { data: requirementsData, isLoading: requirementsLoading } = useQuery({
    queryKey: ['employeeRequirements', employeeId],
    queryFn: () => learningApi.getRequirements({
      employee_id: employeeId,
      limit: 100,
    }),
    enabled: isAdminOrHR && !!employeeId,
  })

  // Fetch training records for this employee for selected year
  const { data: recordsData, isLoading: recordsLoading } = useQuery({
    queryKey: ['employeeTrainingRecords', employeeId, selectedYear],
    queryFn: () => learningApi.getAllRecords({
      employee_id: employeeId,
      year: selectedYear,
      limit: 100,
    }),
    enabled: isAdminOrHR && !!employeeId,
  })

  const requirements = requirementsData?.data || []
  const records = recordsData?.data || []
  const employeeInfo = requirements[0]?.employee || records[0]?.employee

  // Get requirement for selected year
  const currentRequirement = requirements.find(r => r.year === selectedYear)

  // Calculate verified hours for selected year
  const verifiedHours = records
    .filter(r => r.verification_status === 'verified')
    .reduce((sum, r) => sum + (r.training.duration_hours || 0), 0)

  // Mutations
  const createMutation = useMutation({
    mutationFn: learningApi.createRequirement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeRequirements', employeeId] })
      queryClient.invalidateQueries({ queryKey: ['trainingRequirements'] })
      toast.success('Training requirement created successfully')
      setShowAddModal(false)
      setFormData({ employee_id: employeeId || '', year: currentYear, required_hours: 40 })
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to create requirement')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTrainingRequirementInput }) =>
      learningApi.updateRequirement(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeRequirements', employeeId] })
      queryClient.invalidateQueries({ queryKey: ['trainingRequirements'] })
      toast.success('Training requirement updated successfully')
      setEditingRequirement(null)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to update requirement')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: learningApi.deleteRequirement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employeeRequirements', employeeId] })
      queryClient.invalidateQueries({ queryKey: ['trainingRequirements'] })
      toast.success('Training requirement deleted successfully')
      setDeleteConfirm(null)
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to delete requirement')
    },
  })

  // Handlers
  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate({ ...formData, employee_id: employeeId || '' })
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingRequirement) {
      updateMutation.mutate({ id: editingRequirement.id, data: editFormData })
    }
  }

  const openEditModal = (req: { id: string; required_hours: number }) => {
    setEditingRequirement(req)
    setEditFormData({ required_hours: req.required_hours })
  }

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return 'bg-green-500'
    if (percentage >= 50) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  if (!user) return <LoadingPage />
  if (!isAdminOrHR) return null
  if (requirementsLoading || recordsLoading) return <LoadingPage />

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-4">
            <Link to="/learning" className="hover:text-white transition-colors">Learning</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link to="/learning/requirements" className="hover:text-white transition-colors">Requirements</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">{employeeInfo?.full_name || 'Employee Details'}</span>
          </div>

          {employeeInfo && (
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold">
                {employeeInfo.full_name?.charAt(0) || employeeInfo.email.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">{employeeInfo.full_name || 'Unknown'}</h1>
                <p className="text-red-200">{employeeInfo.department || employeeInfo.email}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Training Records */}
          <div className="lg:col-span-2 space-y-6">
            {/* Year Filter and Stats */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Training Records
                </h2>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              {/* Year Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-gray-900">{records.length}</p>
                  <p className="text-sm text-gray-500">Total Trainings</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-green-600">{verifiedHours}h</p>
                  <p className="text-sm text-gray-500">Verified Hours</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-red-600">
                    {currentRequirement ? `${Math.round(currentRequirement.completion_percentage)}%` : '-'}
                  </p>
                  <p className="text-sm text-gray-500">Completion</p>
                </div>
              </div>

              {/* Progress Bar */}
              {currentRequirement && (
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">{currentRequirement.completed_hours}h completed</span>
                    <span className="text-gray-600">{currentRequirement.required_hours}h required</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getProgressColor(currentRequirement.completion_percentage)} transition-all duration-300`}
                      style={{ width: `${Math.min(currentRequirement.completion_percentage, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Training List */}
              {records.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>No training records for {selectedYear}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {records.map((record) => (
                    <div
                      key={record.id}
                      onClick={() => navigate(`/learning/records/${record.id}`, { state: { from: 'all-records' } })}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          record.verification_status === 'verified' ? 'bg-green-100' :
                          record.verification_status === 'rejected' ? 'bg-red-100' : 'bg-yellow-100'
                        }`}>
                          <svg className={`w-5 h-5 ${
                            record.verification_status === 'verified' ? 'text-green-600' :
                            record.verification_status === 'rejected' ? 'text-red-600' : 'text-yellow-600'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{record.training.name}</p>
                          <p className="text-sm text-gray-500">
                            {format(new Date(record.start_date), 'MMM d, yyyy')}
                            {record.training.duration_hours && ` • ${record.training.duration_hours}h`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${VERIFICATION_STATUS_COLORS[record.verification_status]}`}>
                          {VERIFICATION_STATUS_LABELS[record.verification_status]}
                        </span>
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Requirements Management */}
          <div className="space-y-6">
            {/* Yearly Requirements */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                  Training Requirements
                </h3>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Add Requirement"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {requirements.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  <p className="text-sm">No requirements set</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="mt-2 text-red-600 hover:text-red-700 text-sm font-medium"
                  >
                    Add first requirement
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {requirements.sort((a, b) => b.year - a.year).map((req) => (
                    <div
                      key={req.id}
                      className={`p-4 rounded-xl border ${
                        req.year === selectedYear ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900">{req.year}</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEditModal({ id: req.id, required_hours: req.required_hours })}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(req.id)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-600">{req.completed_hours}h / {req.required_hours}h</span>
                        <span className={`font-medium ${
                          req.is_completed ? 'text-green-600' :
                          req.completion_percentage >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {Math.round(req.completion_percentage)}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${getProgressColor(req.completion_percentage)} transition-all duration-300`}
                          style={{ width: `${Math.min(req.completion_percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-red-50 rounded-2xl p-6">
              <h3 className="text-sm font-medium text-red-800 uppercase tracking-wide mb-3">Quick Actions</h3>
              <Link
                to="/learning/all-records"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-white text-red-700 font-medium rounded-xl hover:bg-red-100 transition-colors border border-red-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                View All Records
              </Link>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <button
            onClick={() => navigate('/learning/requirements')}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Requirements
          </button>
        </div>
      </div>

      {/* Add Requirement Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Add Training Requirement</h2>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                <p className="text-gray-900">{employeeInfo?.full_name || employeeInfo?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year} disabled={requirements.some(r => r.year === year)}>
                      {year} {requirements.some(r => r.year === year) && '(exists)'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Required Hours</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.required_hours}
                  onChange={(e) => setFormData({ ...formData, required_hours: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Requirement Modal */}
      {editingRequirement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Edit Training Requirement</h2>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Required Hours</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={editFormData.required_hours}
                  onChange={(e) => setEditFormData({ required_hours: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingRequirement(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete Requirement?</h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to delete this training requirement? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate(deleteConfirm)}
                  disabled={deleteMutation.isPending}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmployeeRequirementDetailPage
